/**
 * components/Icon
 * ──────────────────────────────────────────────────────────────────────────
 * The only way an icon is drawn in this app. One 16×16 stroked path, sized and
 * coloured by props, rendered through react-native-svg so Android, iOS and web
 * produce the same glyph.
 *
 * `opacity` defaults to 1; the sidebar passes 0.72, which is the value the
 * design uses to sit a nav icon behind its label without introducing a second
 * grey into the palette.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { memo } from 'react';
import Svg, { Path } from 'react-native-svg';
import { neutral } from '../../theme/colors';
import { ICON_PATHS, type IconName } from './paths';

export interface IconProps {
  name: IconName;
  /** Rendered size in px. The path grid is always 16×16 and scales to fit. */
  size?: number;
  color?: string;
  /** 1.3 nav · 1.4 chrome · 1.6 the plus glyph. */
  strokeWidth?: number;
  opacity?: number;
}

function IconBase({
  name,
  size = 15,
  color = neutral.ink,
  strokeWidth = 1.4,
  opacity = 1,
}: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 16 16" fill="none" opacity={opacity}>
      <Path
        d={ICON_PATHS[name]}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/**
 * Memoised: the sidebar, every table row and every chip renders one, and the
 * props are primitives, so a shallow compare is both cheap and always correct.
 */
export const Icon = memo(IconBase);
export default Icon;
export type { IconName };
export { ICON_PATHS };
